import type { OperatingParameters } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { MAX_DELTA_REQUIRED_PRESSURE } from "../../constants";
import type {
  HypotheticalFrequencyDataSet,
  HypotheticalFrequencyDataRow
} from "./d";
import { doOneSet } from "./doOneSet";

type FoundThreshold = {
  thresholdRow: HypotheticalFrequencyDataRow;
  thresholdFrequency: number;
  minFrequency: number;
  maxFrequency: number;
};

export const getPredictedDesiredFrequencyHz = (
  scenarioCalculations: ScenarioCalculations,
  _parameters: OperatingParameters
): HypotheticalFrequencyDataSet => {
  const hypotheticalFrequencies = Array.from(
    { length: 26 },
    (_, i) => i * 5 + 25
  );

  // the current strategy is to evaluate the entire top level, then to spiral in to
  // each identified threshold until a result is found or all options are exhausted

  let resultSet = doOneSet(
    hypotheticalFrequencies,
    scenarioCalculations,
    _parameters,
    1,
    true
  );

  const foundThresholds: FoundThreshold[] = [];
  for (let i = 0; i < resultSet.rows.length; i++) {
    const thresholdRow = resultSet.rows[i];
    if (thresholdRow.status === "threshold-found") {
      foundThresholds.push({
        thresholdRow,
        thresholdFrequency: thresholdRow.hypotheticalFrequencyHz,
        minFrequency: thresholdRow.hypotheticalFrequencyHz - 5,
        maxFrequency: thresholdRow.hypotheticalFrequencyHz + 5
      });
    }
  }

  for (let i = 0; i < foundThresholds.length; i++) {
    const foundThreshold = foundThresholds[i];
    const nextMaxFrequency = foundThreshold.maxFrequency;
    const nextMinFrequency = foundThreshold.minFrequency;
    const stepSize = (nextMaxFrequency - nextMinFrequency) / 20;
    const NUM_STEPS = 21;
    const nextHypotheticalFrequencies = Array.from(
      { length: NUM_STEPS },
      (_, index) => nextMinFrequency + index * stepSize
    );

    const thresholdResult = doOneSet(
      nextHypotheticalFrequencies,
      scenarioCalculations,
      _parameters,
      1
    );

    if (thresholdResult.status === "result-found") {
      resultSet = {
        ...resultSet,
        status: "result-found",
        targetRow: thresholdResult.targetRow,
        rows: [
          ...resultSet.rows.filter(
            r => r.hypotheticalFrequencyHz < foundThreshold.minFrequency
          ),
          ...thresholdResult.rows
        ]
      };

      break;
    } else {
      resultSet = {
        ...resultSet,
        status: thresholdResult.status,
        targetRow: thresholdResult.targetRow,
        rows: [
          ...resultSet.rows.filter(
            r => r.hypotheticalFrequencyHz < foundThreshold.thresholdFrequency
          ),
          { ...foundThreshold.thresholdRow, status: "false-threshold" },
          ...thresholdResult.rows,
          ...resultSet.rows.filter(
            r => r.hypotheticalFrequencyHz > foundThreshold.thresholdFrequency
          )
        ]
      };
    }
  }

  const result: HypotheticalFrequencyDataSet = {
    ...resultSet,
    importantStuff: {
      MAX_DELTA_REQUIRED_PRESSURE
    }
  };

  return result;
};

export * from "./d";
